// @ts-nocheck
/**
 * Print pack — everything a director hands out on the weekend, from one
 * read-only payload (`print_pack` on rle-operator-schedule-draft).
 *
 * This page never writes or recomputes standings. Its rows come from the same
 * shared ranking engine as the public and operator standings screens.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Printer, RefreshCw, Loader2, FileSpreadsheet } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { formatTime, formatCalendarDate } from '@/lib/dates';
import { formatUsPhone } from '@/lib/phone';
import {
  CSV_DOCS, csvFileName, downloadCsv, gridCsv, rostersCsv, scheduleCsv, standingsCsv,
  registrationsCsv, rosterCountsCsv, teamSummaryCsv, teamSummaryRows,
  divisionSummaryCsv, divisionSummaryRows, gamesPerDayCsv,
} from '@/lib/printPackCsv';
import { overLimitRows, backToBackRows, limitsFor } from '@/lib/gamesPerDay';
import { toast } from 'sonner';
import { useDivisionColors } from '@/hooks/useDivisionColors';
import { resolveDivisionColors } from '@/lib/divisionPalette';
import { formatGameNumber } from '@/lib/gameNumber';

const FN = 'rle-operator-schedule-draft';

/** Reports about teams rather than times — the day filter does not apply. */
const TEAM_DOCS = ['registrations', 'rostercounts', 'teamsummary', 'divisionsummary', 'gamesperday'];

const DOCS = [
  { key: 'master', label: 'Master schedule', hint: 'Every game, by day and time' },
  { key: 'division', label: 'Schedule by division', hint: 'One section per division' },
  { key: 'court', label: 'Schedule by court', hint: 'One section per court' },
  { key: 'pool', label: 'Schedule by pool', hint: 'Pool play only' },
  { key: 'grid', label: 'Master grid', hint: 'Time down, courts across — one page per day' },
  { key: 'brackets', label: 'Brackets', hint: 'Bracket games by round' },
  { key: 'standings', label: 'Standings', hint: 'From the live standings engine' },
  { key: 'rosters', label: 'Team rosters', hint: 'One page per team' },
  { key: 'scoresheets', label: 'Score sheets', hint: 'One page per game' },
  { key: 'registrations', label: 'Registration list', hint: 'Coach contacts and money — office copy' },
  { key: 'rostercounts', label: 'Roster counts', hint: 'Players per team, locked, checked in' },
  { key: 'teamsummary', label: 'Team summary', hint: 'Team, pool, coach and record' },
  { key: 'divisionsummary', label: 'Division & pool summary', hint: 'Teams, pools and games completed' },
  { key: 'gamesperday', label: 'Games per day (NCAA)', hint: 'Proof that no team plays over the limit' },
];


const usd = (n: any) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n ?? 0));

const timeLabel = (t: string | null) => (t ? formatTime(t) : 'TBD');
const dayLabel = (d: string | null) => (d ? formatCalendarDate(d) : 'Unscheduled');

function DocSection({ title, subtitle, children }: any) {
  return (
    <section className="page-break-before mb-8">
      <h2 className="text-lg font-bold border-b pb-1 mb-3">{title}</h2>
      {subtitle && <p className="text-xs text-muted-foreground mb-2">{subtitle}</p>}
      {children}
    </section>
  );
}

/**
 * Print colour is an ADDITION to the division text, never a replacement:
 * a black-and-white office printer must still show which division a game is.
 */
function DivisionMark({ division, color }: { division: string; color?: any }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        aria-hidden
        className="inline-block h-2.5 w-2.5 rounded-full border border-black/30"
        style={{ background: color?.stripe, printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
      />
      {division}
    </span>
  );
}

function GameTable({ games, showCourt = true, showDivision = true, colorFor }: any) {
  if (!games.length) return <p className="text-sm text-muted-foreground">No games.</p>;
  return (
    <table className="w-full text-sm border-collapse mb-4">
      <thead>
        <tr className="text-left border-b">
          <th className="py-1 pr-3">Game</th>
          <th className="py-1 pr-3">Time</th>
          {showCourt && <th className="py-1 pr-3">Court</th>}
          {showDivision && <th className="py-1 pr-3">Division</th>}
          <th className="py-1 pr-3">Matchup</th>
          <th className="py-1">Pool / Round</th>
        </tr>
      </thead>
      <tbody>
        {games.map((g: any) => (
          <tr key={g.id} className="border-b last:border-0">
            <td className="py-1 pr-3 whitespace-nowrap font-medium">{formatGameNumber(g) || '—'}</td>
            <td className="py-1 pr-3 whitespace-nowrap">{timeLabel(g.scheduled_start_time)}</td>
            {showCourt && <td className="py-1 pr-3">{g.court_name ?? '—'}</td>}
            {showDivision && (
              <td className="py-1 pr-3">
                <DivisionMark division={g.division} color={colorFor?.(g.division)} />
              </td>
            )}
            <td className="py-1 pr-3">{g.team_a_name} vs {g.team_b_name}</td>
            <td className="py-1">
              {g.game_type === 'crossover'
                ? <span className="font-semibold text-red-600">Crossover</span>
                : (g.bracket_label ?? g.pool_name ?? g.game_type)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function OperatorEventPrintPackPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { stored: divisionColorOverrides } = useDivisionColors(eventId);
  /** Print is always the light palette — paper is white. */
  const colorFor = (d: string) => resolveDivisionColors(d, divisionColorOverrides[d] ?? null, 'light');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pack, setPack] = useState<any>(null);
  const [selected, setSelected] = useState<string[]>(['master', 'grid']);
  const [division, setDivision] = useState('all');
  const [day, setDay] = useState('all');

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await invokeEdgeFunction(FN, { body: { action: 'print_pack', event_id: eventId } });
      if (!res?.data?.ok) throw new Error(res?.data?.error?.message || 'Could not load the print pack.');
      setPack(res.data.data);
    } catch (e: any) {
      setError(e?.message || 'Could not load the print pack.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const s = new Set<string>();
    for (const g of pack?.games ?? []) if (g.scheduled_date) s.add(g.scheduled_date);
    return [...s].sort();
  }, [pack]);

  const games = useMemo(() => {
    let list = (pack?.games ?? []) as any[];
    if (division !== 'all') list = list.filter((g) => g.division === division);
    if (day !== 'all') list = list.filter((g) => g.scheduled_date === day);
    return [...list].sort((a, b) =>
      String(a.scheduled_date ?? 'zzzz').localeCompare(String(b.scheduled_date ?? 'zzzz')) ||
      String(a.scheduled_start_time ?? 'zz').localeCompare(String(b.scheduled_start_time ?? 'zz')) ||
      String(a.court_name ?? '').localeCompare(String(b.court_name ?? '')));
  }, [pack, division, day]);

  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const g of games) {
      const k = g.scheduled_date ?? '';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(g);
    }
    return [...m.entries()].sort((a, b) => String(a[0] || 'zzzz').localeCompare(String(b[0] || 'zzzz')));
  }, [games]);

  /**
   * The four operator reports. They are about teams, not times, so only the
   * division filter applies — the day filter is deliberately ignored.
   */
  const regs = useMemo(
    () => ((pack?.registrations ?? []) as any[]).filter(
      (r) => division === 'all' || r.division === division,
    ),
    [pack, division],
  );

  const teamSummary = useMemo(
    () => teamSummaryRows(regs, pack?.standings ?? [], pack?.pools ?? []),
    [regs, pack],
  );

  const divSummary = useMemo(() => {
    const divs = ((pack?.divisions ?? []) as string[]).filter(
      (d) => division === 'all' || d === division,
    );
    return divisionSummaryRows(divs, regs, pack?.pools ?? [], pack?.games ?? []);
  }, [pack, division, regs]);



  /**
   * The NCAA compliance report. Whole event, every day — a compliance document
   * that only covered one day would prove nothing — filtered by division only.
   */
  const compliance = pack?.compliance ?? null;
  const complianceGames = useMemo(
    () => ((pack?.games ?? []) as any[]).filter((g) => division === 'all' || g.division === division),
    [pack, division],
  );
  const overLimit = useMemo(() => overLimitRows(complianceGames, compliance), [complianceGames, compliance]);
  const tooClose = useMemo(() => backToBackRows(complianceGames, compliance), [complianceGames, compliance]);
  const complianceLimit = limitsFor(compliance, division === 'all' ? '' : division);
  const gameLine = (r: any) =>
    [r.game_number != null ? `Game ${r.game_number}` : 'Game', r.bracket_label, timeLabel(r.time), r.court || 'No court']
      .filter(Boolean).join(' · ');
  const cleanSentence = complianceLimit.maxPerDay
    ? `No team exceeds ${complianceLimit.maxPerDay} games in a day.`
    : 'No games-per-day limit is set for this event, so there is nothing to check. Set one on Schedule setup.';

  const has = (k: string) => selected.includes(k);
  const toggle = (k: string) =>
    setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const doPrint = () => {
    if (!selected.length) { toast.error('Pick at least one document.'); return; }
    window.print();
  };

  /**
   * CSV of whatever is ticked, from the payload already in memory and the same
   * day/division filters as the print view. One file per document; score sheets
   * and brackets are deliberately print-only.
   */
  const doDownloadCsv = () => {
    const docs = selected.filter((k) => CSV_DOCS.includes(k));
    if (!docs.length) {
      toast.error('Pick a document that exports — score sheets and brackets are print-only.');
      return;
    }
    const name = pack?.event?.name ?? 'event';
    let files = 0;

    for (const key of docs) {
      if (key === 'master' || key === 'division' || key === 'court') {
        downloadCsv(csvFileName(name, `${key}-schedule`), scheduleCsv(games, key));
        files++;
      } else if (key === 'pool') {
        downloadCsv(
          csvFileName(name, 'pool-schedule'),
          scheduleCsv(games.filter((g: any) => g.game_type === 'pool'), 'pool'),
        );
        files++;
      } else if (key === 'grid') {
        for (const [d, gs] of byDay) {
          downloadCsv(csvFileName(name, 'master-grid', d || 'unscheduled'), gridCsv(gs, pack?.courts ?? []));
          files++;
        }
      } else if (key === 'standings') {
        downloadCsv(
          csvFileName(name, 'standings'),
          standingsCsv((pack?.standings ?? []).filter((s: any) => division === 'all' || s.division === division)),
        );
        files++;
      } else if (key === 'rosters') {
        downloadCsv(
          csvFileName(name, 'rosters'),
          rostersCsv((pack?.rosters ?? []).filter((r: any) => division === 'all' || r.division === division)),
        );
        files++;
      } else if (key === 'registrations') {
        downloadCsv(csvFileName(name, 'registration-list'), registrationsCsv(regs));
        files++;
      } else if (key === 'rostercounts') {
        downloadCsv(csvFileName(name, 'roster-counts'), rosterCountsCsv(regs));
        files++;
      } else if (key === 'teamsummary') {
        downloadCsv(csvFileName(name, 'team-summary'), teamSummaryCsv(teamSummary));
        files++;
      } else if (key === 'divisionsummary') {
        downloadCsv(csvFileName(name, 'division-summary'), divisionSummaryCsv(divSummary));
        files++;
      } else if (key === 'gamesperday') {
        downloadCsv(
          csvFileName(name, 'games-per-day-ncaa'),
          gamesPerDayCsv(
            overLimit.map((r: any) => ({
              team: r.team, division: r.division, date: dayLabel(r.date),
              games: r.games, limit: r.limit,
              detail: r.rows.map(gameLine).join(' | '),
            })),
            cleanSentence,
          ),
        );
        files++;
      }

    }

    const skipped = selected.filter((k) => !CSV_DOCS.includes(k)).length;
    toast.success(
      `${files} file${files === 1 ? '' : 's'} downloaded${skipped ? ` · ${skipped} print-only document(s) skipped` : ''}`,
    );
  };


  if (loading) {
    return <div className="p-6 space-y-3"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive mb-3">{error}</p>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Try again</Button>
      </div>
    );
  }

  const rosters = (pack?.rosters ?? []).filter((r: any) => division === 'all' || r.division === division);
  const standings = (pack?.standings ?? []).filter((s: any) => division === 'all' || s.division === division);
  const brackets = games.filter((g) => g.game_type === 'bracket');
  const poolGames = games.filter((g) => g.game_type === 'pool');

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* ---- Controls: never printed ---- */}
      <div className="print:hidden space-y-4 mb-6">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
            <Button variant="outline" size="sm" onClick={doDownloadCsv}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />Download CSV
            </Button>
            <Button size="sm" onClick={doPrint}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Print pack</CardTitle>
            <p className="text-sm text-muted-foreground">
              Tick the documents you want, then Print. Nothing here changes the schedule.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {DOCS.map((d) => (
                <label key={d.key} className="flex items-start gap-2 rounded-md border p-2 cursor-pointer">
                  <Checkbox checked={has(d.key)} onCheckedChange={() => toggle(d.key)} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium">{d.label}</span>
                    <span className="block text-xs text-muted-foreground">{d.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="w-52">
                <Select value={division} onValueChange={setDivision}>
                  <SelectTrigger><SelectValue placeholder="All divisions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All divisions</SelectItem>
                    {(pack?.divisions ?? []).map((d: string) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-52">
                <Select value={day} onValueChange={setDay}>
                  <SelectTrigger><SelectValue placeholder="All days" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All days</SelectItem>
                    {days.map((d) => (
                      <SelectItem key={d} value={d}>{dayLabel(d)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selected.some((k) => TEAM_DOCS.includes(k)) && (
                <span className="text-xs text-muted-foreground">
                  The day filter has no effect on the registration, roster count, team and division reports.
                </span>
              )}
              {pack?.is_draft && <Badge variant="outline" className="text-amber-600 border-amber-500">Draft — prints stamped</Badge>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ---- The printable pack ---- */}
      <div className="bg-background">
        <header className="mb-6">
          <h1 className="text-xl font-bold">{pack?.event?.name ?? 'Event'}</h1>
          <p className="text-sm text-muted-foreground">
            {pack?.event?.start_date ? formatCalendarDate(pack.event.start_date) : ''}
            {pack?.event?.end_date && pack.event.end_date !== pack.event.start_date
              ? ` – ${formatCalendarDate(pack.event.end_date)}` : ''}
            {pack?.is_draft ? ' · DRAFT — not published' : ''}
          </p>
        </header>

        {has('master') && (
          <DocSection title="Master schedule">
            {byDay.map(([d, gs]) => (
              <div key={d || 'none'} className="mb-4">
                <h3 className="font-semibold text-sm mb-1">{dayLabel(d || null)}</h3>
                <GameTable games={gs} colorFor={colorFor} />
              </div>
            ))}
          </DocSection>
        )}

        {has('division') && (pack?.divisions ?? []).map((div: string) =>
          (division === 'all' || division === div) ? (
            <DocSection key={div} title={`Schedule — ${div}`}>
              <div className="mb-2 text-xs">
                <DivisionMark division={div} color={colorFor(div)} />
              </div>
              <GameTable games={games.filter((g) => g.division === div)} showDivision={false} />
            </DocSection>
          ) : null)}

        {has('court') && (pack?.courts ?? []).map((c: any) => (
          <DocSection key={c.id} title={`Schedule — ${c.name}`} subtitle={c.venue_name ?? undefined}>
            <GameTable games={games.filter((g) => g.court_id === c.id)} showCourt={false} colorFor={colorFor} />
          </DocSection>
        ))}

        {has('pool') && (pack?.pools ?? [])
          .filter((p: any) => division === 'all' || p.division === division)
          .map((p: any) => (
            <DocSection key={p.id} title={`${p.division} — ${p.name}`}>
              <p className="text-sm mb-2">
                {p.teams.map((t: any) => t.team_name).join(' · ') || 'No teams'}
              </p>
              <GameTable games={poolGames.filter((g) => g.pool_id === p.id)} showDivision={false} colorFor={colorFor} />
            </DocSection>
          ))}

        {has('grid') && byDay.map(([d, gs]) => {
          const courts = (pack?.courts ?? []).filter((c: any) => gs.some((g: any) => g.court_id === c.id));
          const times = [...new Set(gs.map((g: any) => g.scheduled_start_time).filter(Boolean))].sort();
          return (
            <DocSection key={`grid-${d}`} title={`Master grid — ${dayLabel(d || null)}`}>
              {courts.length === 0 || times.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing placed on this day.</p>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="border p-1 text-left">Time</th>
                      {courts.map((c: any) => <th key={c.id} className="border p-1 text-left">{c.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {times.map((t: string) => (
                      <tr key={t}>
                        <td className="border p-1 whitespace-nowrap">{timeLabel(t)}</td>
                        {courts.map((c: any) => {
                          const g = gs.find((x: any) => x.court_id === c.id && x.scheduled_start_time === t);
                          return (
                            <td key={c.id} className="border p-1 align-top">
                              {g ? (
                                <>
                                  {formatGameNumber(g) && (
                                    <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                                      {formatGameNumber(g)}
                                    </div>
                                  )}
                                  <div>{g.team_a_name}</div>
                                  <div>{g.team_b_name}</div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {g.division}
                                    {g.game_type === 'crossover'
                                      ? <> · <span className="font-semibold text-red-600">Crossover</span></>
                                      : g.pool_name ? ` · ${g.pool_name}` : ''}
                                  </div>
                                </>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </DocSection>
          );
        })}

        {has('brackets') && (
          <DocSection title="Brackets">
            {brackets.length === 0 ? (
              <p className="text-sm text-muted-foreground">No bracket games yet.</p>
            ) : (
              [...new Set(brackets.map((g) => g.bracket_round ?? 0))].sort((a, b) => a - b).map((r) => (
                <div key={r} className="mb-3">
                  <h3 className="font-semibold text-sm mb-1">Round {r}</h3>
                  <GameTable games={brackets.filter((g) => (g.bracket_round ?? 0) === r)} />
                </div>
              ))
            )}
          </DocSection>
        )}

        {has('standings') && standings.map((s: any) => (
          <DocSection key={s.division} title={`Standings — ${s.division}`}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-1 pr-3">#</th>
                  <th className="py-1 pr-3">Team</th>
                  <th className="py-1 pr-3">Pool</th>
                  <th className="py-1 pr-3">W-L-T</th>
                  <th className="py-1 pr-3">PF</th>
                  <th className="py-1 pr-3">PA</th>
                  <th className="py-1">Diff</th>
                </tr>
              </thead>
              <tbody>
                {s.rows.map((r: any) => (
                  <tr key={r.registration_id} className="border-b last:border-0">
                    <td className="py-1 pr-3">{r.pool_rank ?? '—'}</td>
                    <td className="py-1 pr-3">{r.team_name}</td>
                    <td className="py-1 pr-3">{r.pool_name ?? '—'}</td>
                    <td className="py-1 pr-3">{r.wins}-{r.losses}-{r.ties}</td>
                    <td className="py-1 pr-3">{r.points_for}</td>
                    <td className="py-1 pr-3">{r.points_against}</td>
                    <td className="py-1">{r.diff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DocSection>
        ))}

        {has('rosters') && rosters.map((r: any) => (
          <DocSection
            key={r.registration_id}
            title={r.team_name}
            subtitle={[r.division, r.coach_name ? `Coach: ${r.coach_name}` : null].filter(Boolean).join(' · ')}
          >
            {r.players.length === 0 ? (
              <p className="text-sm text-muted-foreground">No players on this roster.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-3">#</th>
                    <th className="py-1 pr-3">Player</th>
                    <th className="py-1">Pos</th>
                  </tr>
                </thead>
                <tbody>
                  {r.players.map((p: any, i: number) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-3">{p.jersey_number ?? '—'}</td>
                      <td className="py-1 pr-3">{p.name}{p.is_captain ? ' (C)' : ''}</td>
                      <td className="py-1">{p.position ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DocSection>
        ))}

        {has('scoresheets') && games.map((g: any) => {
          const periods = Number(pack?.format?.period_count ?? 2) || 2;
          const periodWord = pack?.format?.period_type === 'quarter' ? 'Q' : 'H';
          return (
            <DocSection
              key={`sheet-${g.id}`}
              title={`Score sheet — ${formatGameNumber(g) ? `${formatGameNumber(g)} · ` : ''}${g.team_a_name} vs ${g.team_b_name}`}
              subtitle={`${dayLabel(g.scheduled_date)} · ${timeLabel(g.scheduled_start_time)} · ${g.court_name ?? 'Court TBD'} · ${g.division}${g.game_type === 'crossover' ? ' · Crossover' : g.pool_name ? ` · ${g.pool_name}` : ''}`}
            >
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="border p-2 text-left w-1/3">Team</th>
                    {Array.from({ length: periods }).map((_, i) => (
                      <th key={i} className="border p-2 text-left">{periodWord}{i + 1}</th>
                    ))}
                    <th className="border p-2 text-left">Final</th>
                  </tr>
                </thead>
                <tbody>
                  {[g.team_a_name, g.team_b_name].map((t: string, i: number) => (
                    <tr key={i}>
                      <td className="border p-2 h-10">{t}</td>
                      {Array.from({ length: periods }).map((_, j) => <td key={j} className="border p-2" />)}
                      <td className="border p-2" />
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-xs text-muted-foreground">
                Winner: ______________________  Scorekeeper: ______________________
              </div>
            </DocSection>
          );
        })}

        {/* ---- Operator reports. Teams, not times: the day filter is ignored. ---- */}

        {has('registrations') && (
          <DocSection
            title="Registration list"
            subtitle="Office copy — contact details and money. Sorted by division, then team."
          >
            {regs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No registrations.</p>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-2">Team</th>
                    <th className="py-1 pr-2">Division</th>
                    <th className="py-1 pr-2">Level</th>
                    <th className="py-1 pr-2">Coach</th>
                    <th className="py-1 pr-2">Email</th>
                    <th className="py-1 pr-2">Phone</th>
                    <th className="py-1 pr-2">City</th>
                    <th className="py-1 pr-2">State</th>
                    <th className="py-1 pr-2">Status</th>
                    <th className="py-1 pr-2">Payment</th>
                    <th className="py-1 pr-2 text-right">Due</th>
                    <th className="py-1 pr-2 text-right">Paid</th>
                    <th className="py-1 pr-2 text-right">Outstanding</th>
                    <th className="py-1">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r: any) => (
                    <tr key={r.registration_id} className="border-b last:border-0">
                      <td className="py-1 pr-2">{r.team_name}</td>
                      <td className="py-1 pr-2">{r.division}</td>
                      <td className="py-1 pr-2">{r.level ?? '—'}</td>
                      <td className="py-1 pr-2">{r.coach_name ?? '—'}</td>
                      <td className="py-1 pr-2">{r.coach_email ?? '—'}</td>
                      <td className="py-1 pr-2">{r.coach_phone ? formatUsPhone(r.coach_phone) : '—'}</td>
                      <td className="py-1 pr-2">{r.city ?? '—'}</td>
                      <td className="py-1 pr-2">{r.state ?? '—'}</td>
                      <td className="py-1 pr-2">{r.registration_status ?? '—'}</td>
                      <td className="py-1 pr-2">{r.payment_status ?? '—'}</td>
                      <td className="py-1 pr-2 text-right">{usd(r.amount_due)}</td>
                      <td className="py-1 pr-2 text-right">{usd(r.amount_paid)}</td>
                      <td className="py-1 pr-2 text-right">{usd(r.amount_outstanding)}</td>
                      <td className="py-1">{r.registered_at ? formatCalendarDate(String(r.registered_at).slice(0, 10)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DocSection>
        )}

        {has('rostercounts') && (
          <DocSection title="Roster counts" subtitle="Active players only. A zero is a finding.">
            {regs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No registrations.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-3">Team</th>
                    <th className="py-1 pr-3">Division</th>
                    <th className="py-1 pr-3 text-right">Players</th>
                    <th className="py-1 pr-3">Roster locked</th>
                    <th className="py-1">Checked in</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r: any) => (
                    <tr key={r.registration_id} className="border-b last:border-0">
                      <td className="py-1 pr-3">{r.team_name}</td>
                      <td className="py-1 pr-3">{r.division}</td>
                      <td className="py-1 pr-3 text-right">{r.player_count ?? 0}</td>
                      <td className="py-1 pr-3">{r.roster_locked ? 'Yes' : 'No'}</td>
                      <td className="py-1">{r.checked_in_at ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DocSection>
        )}

        {has('teamsummary') && (
          <DocSection title="Team summary" subtitle="Records come from the live standings engine.">
            {teamSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground">No registrations.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-3">Team</th>
                    <th className="py-1 pr-3">Division</th>
                    <th className="py-1 pr-3">Pool</th>
                    <th className="py-1 pr-3">Level</th>
                    <th className="py-1 pr-3">Coach</th>
                    <th className="py-1 pr-3">W-L-T</th>
                    <th className="py-1 pr-3 text-right">GP</th>
                    <th className="py-1 pr-3 text-right">PF</th>
                    <th className="py-1 pr-3 text-right">PA</th>
                    <th className="py-1 text-right">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {teamSummary.map((r: any, i: number) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-3">{r.team_name}</td>
                      <td className="py-1 pr-3">{r.division}</td>
                      <td className="py-1 pr-3">{r.pool_name ?? '—'}</td>
                      <td className="py-1 pr-3">{r.level ?? '—'}</td>
                      <td className="py-1 pr-3">{r.coach_name ?? '—'}</td>
                      <td className="py-1 pr-3">{r.wins}-{r.losses}-{r.ties}</td>
                      <td className="py-1 pr-3 text-right">{r.games_played}</td>
                      <td className="py-1 pr-3 text-right">{r.points_for}</td>
                      <td className="py-1 pr-3 text-right">{r.points_against}</td>
                      <td className="py-1 text-right">{r.diff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DocSection>
        )}

        {has('gamesperday') && (
          <DocSection
            title="Games per day — NCAA compliance"
            subtitle={
              (complianceLimit.maxPerDay
                ? `Limit in force: ${complianceLimit.maxPerDay} games per team per day.`
                : 'No games-per-day limit is set on this event.') +
              (complianceLimit.backToBack > 0
                ? ` Least time between a team's games: ${complianceLimit.backToBack} min.`
                : ' Back-to-back games are allowed.') +
              ` ${pack?.event?.name ?? 'Event'}` +
              (pack?.event?.start_date ? ` · ${formatCalendarDate(pack.event.start_date)}` : '') +
              (pack?.event?.end_date && pack.event.end_date !== pack.event.start_date
                ? ` – ${formatCalendarDate(pack.event.end_date)}` : '') +
              (division === 'all' ? ' · all divisions' : ` · ${division}`)
            }
          >
            {overLimit.length === 0 ? (
              <p className="text-sm font-medium">{cleanSentence}</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-3">Team</th>
                    <th className="py-1 pr-3">Division</th>
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3 text-right">Games</th>
                    <th className="py-1 pr-3 text-right">Limit</th>
                    <th className="py-1">Games that day</th>
                  </tr>
                </thead>
                <tbody>
                  {overLimit.map((r: any, i: number) => (
                    <tr key={`${r.team}-${r.date}-${i}`} className="border-b last:border-0 align-top">
                      <td className="py-1 pr-3">{r.team}</td>
                      <td className="py-1 pr-3">{r.division}</td>
                      <td className="py-1 pr-3">{dayLabel(r.date)}</td>
                      <td className="py-1 pr-3 text-right">{r.games}</td>
                      <td className="py-1 pr-3 text-right">{r.limit}</td>
                      <td className="py-1">
                        {r.rows.map((g: any, j: number) => (
                          <div key={j}>{gameLine(g)}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 className="font-semibold text-sm mt-4 mb-1">Back-to-back games</h3>
            {complianceLimit.backToBack <= 0 ? (
              <p className="text-sm text-muted-foreground">
                No minimum gap is set, so back-to-back games are allowed and nothing is reported.
              </p>
            ) : tooClose.length === 0 ? (
              <p className="text-sm font-medium">
                No team plays two games less than {complianceLimit.backToBack} minutes apart.
              </p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-1 pr-3">Team</th>
                    <th className="py-1 pr-3">Division</th>
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3 text-right">Gap</th>
                    <th className="py-1">Games</th>
                  </tr>
                </thead>
                <tbody>
                  {tooClose.map((r: any, i: number) => (
                    <tr key={`${r.team}-${r.date}-${i}`} className="border-b last:border-0 align-top">
                      <td className="py-1 pr-3">{r.team}</td>
                      <td className="py-1 pr-3">{r.division}</td>
                      <td className="py-1 pr-3">{dayLabel(r.date)}</td>
                      <td className="py-1 pr-3 text-right">{r.gap_minutes} min</td>
                      <td className="py-1">{gameLine(r.first)} → {gameLine(r.second)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DocSection>
        )}

        {has('divisionsummary') && (
          <DocSection title="Division & pool summary" subtitle="Is this division actually ready?">
            {divSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground">No divisions.</p>
            ) : (
              divSummary.map((d: any) => (
                <div key={d.division} className="mb-4">
                  <h3 className="font-semibold text-sm mb-1">
                    {d.division} — {d.teams} team{d.teams === 1 ? '' : 's'} · {d.pools} pool{d.pools === 1 ? '' : 's'} ·{' '}
                    {d.games} game{d.games === 1 ? '' : 's'} · {d.completed} completed
                  </h3>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-1 pr-3">Pool</th>
                        <th className="py-1 pr-3 text-right">Teams</th>
                        <th className="py-1 pr-3 text-right">Games</th>
                        <th className="py-1 text-right">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.length === 0 ? (
                        <tr><td colSpan={4} className="py-1 text-muted-foreground">No pools.</td></tr>
                      ) : d.rows.map((p: any) => (
                        <tr key={p.pool_name} className="border-b last:border-0">
                          <td className="py-1 pr-3">{p.pool_name}</td>
                          <td className="py-1 pr-3 text-right">{p.teams}</td>
                          <td className="py-1 pr-3 text-right">{p.games}</td>
                          <td className="py-1 text-right">{p.completed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </DocSection>
        )}
      </div>

    </div>
  );
}
